import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { useTranslation } from "react-i18next";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { API_BASE_URL } from "../config/apiConfig";
import axios from "axios";
import HomeButton from "../components/HomeButton";

interface Chat {
  id: string;
  name: string;
}

interface Message {
  role: string;
  content: string;
}

const ChatInterface: React.FC = () => {
  const { t } = useTranslation();
  const [stompClient, setStompClient] = useState<Client | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    fetchChats();

    const socket = new SockJS(`${API_BASE_URL}/ws`);
    const client = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 2000,
      onConnect: () => {
        console.log("Connected to WebSocket");
        client.subscribe("/topic/responses", (message) => {
          const match = message.body.match(/^data:(.*)/);
          if (match) {
            setMessages((prev) => [
              ...prev,
              { role: "bot", content: match[1] },
            ]);
          }
        });
      },
      onStompError: (frame) => {
        console.error("WebSocket error:", frame);
      },
    });

    client.activate();
    setStompClient(client);

    return () => {
      client.deactivate();
    };
  }, []);

  const fetchChats = async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get(`${API_BASE_URL}/zhadiger/api/chats`, {
      headers: {
        Authorization: `${token}`,
        "ngrok-skip-browser-warning": "true",
      },
      withCredentials: true,
    });
    setChats(data);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newChatName, setNewChatName] = useState("");

  const startNewChat = async () => {
    if (!newChatName.trim()) {
      console.error("Chat name cannot be empty");
      return;
    }

    const token = localStorage.getItem("token");
    const { data } = await axios.post(
      `${API_BASE_URL}/zhadiger/api/chats?name=${encodeURIComponent(
        newChatName
      )}`,
      {},
      {
        headers: {
          Authorization: `${token}`,
          "ngrok-skip-browser-warning": "true",
        },
      }
    );
    setCurrentChatId(data);
    setMessages([]);
    fetchChats();
    setIsModalOpen(false);
    setNewChatName("");
  };

  const loadChat = async (chatId: string) => {
    setCurrentChatId(chatId);
    const token = localStorage.getItem("token");
    const { data } = await axios.get(
      `${API_BASE_URL}/zhadiger/api/chats/${chatId}`,
      {
        headers: {
          Authorization: `${token}`,
          "ngrok-skip-browser-warning": "true",
        },
        withCredentials: true,
      }
    );

    const formattedMessages: Message[] = data.messages.flatMap(
      (msg: { inputText: string; summary: string }) => [
        { role: "user", content: msg.inputText },
        { role: "bot", content: msg.summary },
      ]
    );

    setMessages(formattedMessages);
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    if (!currentChatId) {
      console.error("No chat selected");
      return;
    }

    const newUserMessage = { role: "user", content: values.inputText };
    setMessages((prev) => [...prev, newUserMessage]);

    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}/zhadiger/api/llm/stream`, {
      method: "POST",
      headers: {
        Authorization: `${token}`,
        credentials: "include",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ chatId: currentChatId, texts: values.inputText }),
    });
    resetForm({});

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader?.read()!;
      if (done) break;
      const text = decoder.decode(value);
      const dataMatches = text.match(/data:(.*)/g);
      if (dataMatches) {
        const botResponse = dataMatches
          .map((match) => match.replace("data:", ""))
          .join("");
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.role === "bot") {
            return [
              ...prev.slice(0, -1),
              { ...lastMessage, content: lastMessage.content + botResponse },
            ];
          } else {
            return [...prev, { role: "bot", content: botResponse }];
          }
        });
      }
    }
  };

  const handleSearchHistory = async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.post(
      `${API_BASE_URL}/zhadiger/api/rag/search`,
      {
        query: searchQuery,
        top_k: 5,
      },
      {
        headers: { Authorization: `${token}` },
        withCredentials: true,
      }
    );
    setSearchResults(data);
    if (data.length > 0) {
      const textId = data[0].text_id;
      const chatResponse = await axios.get(
        `${API_BASE_URL}/zhadiger/api/chats/by-llm/${textId}`,
        {
          headers: {
            Authorization: `${token}`,
            "ngrok-skip-browser-warning": "true",
          },
          withCredentials: true,
        }
      );
      setCurrentChatId(chatResponse.data.id);
      loadChat(chatResponse.data.id);
    }
  };

  return (
    <>
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-1/3">
            <h2 className="text-white text-lg font-bold mb-4">
              {t("Enter chat name")}
            </h2>
            <input
              type="text"
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              placeholder={"Enter chat name"}
              className="w-full p-3 border border-gray-500 rounded-lg bg-gray-600 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-4"
            />
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={startNewChat}
                className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
              >
                {t("Create")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-screen bg-gray-700">
        <div className="w-1/4 bg-gray-700 shadow-lg flex flex-col">
          <div className="p-4 border-b border-gray-600 flex space-x-2">
            <HomeButton />
            <button
              className="flex-1 bg-blue-500 text-white py-2 h-full rounded-lg hover:bg-blue-700 transition"
              onClick={() => setIsModalOpen(true)}
            >
              + {t("start_new_chat")}
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto">
            {chats.map((chat) => (
              <li
                key={chat.id}
                className={`p-4 cursor-pointer hover:bg-blue-800 transition ${
                  chat.id === currentChatId
                    ? "bg-blue-700 font-bold text-white"
                    : "text-gray-200"
                }`}
                onClick={() => loadChat(chat.id)}
              >
                {chat.name || `Chat ${chat.id}`}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="p-4 bg-gray-600 text-white text-center text-xl font-bold">
            {t("llm_chat")}
          </div>
          {currentChatId ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 flex flex-col">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg max-w-lg ${
                      msg.role === "user"
                        ? "bg-blue-500 text-white self-end text-right"
                        : "bg-gray-600 text-gray-200 self-start text-left"
                    }`}
                  >
                    {msg.content}
                  </div>
                ))}
              </div>
              <Formik initialValues={{ inputText: "" }} onSubmit={handleSubmit}>
                {({ isSubmitting }) => (
                  <Form className="p-4 flex border-t border-gray-500 bg-gray-600">
                    <Field
                      name="inputText"
                      type="text"
                      placeholder="Type a message..."
                      className="flex-1 p-3 border border-gray-500 rounded-l-lg bg-gray-600 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-blue-500 text-white px-6 py-3 rounded-r-lg hover:bg-blue-700 transition"
                    >
                      {t("send")}
                    </button>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex-1 flex items-center justify-center flex-col">
                <p className="text-gray-400 mb-4">{t("no_chat_seleted")}</p>
                {/* <button
                  className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
                  onClick={startNewChat}
                >
                  + {t("start_new_chat")}
                </button> */}
              </div>
            </div>
          )}
        </div>

        <div className="w-1/4 bg-gray-700 shadow-lg flex flex-col">
          <div className="p-4 border-b border-gray-600">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("search") || "Search"}
              className="w-full p-3 border border-gray-500 rounded-lg bg-gray-600 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              onClick={handleSearchHistory}
              className="w-full bg-blue-500 text-white py-2 mt-2 rounded-lg hover:bg-blue-700 transition"
            >
              {t("search")}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <h2 className="text-lg font-bold text-gray-200 mb-4">
              {t("search_results")}
            </h2>
            <ul className="space-y-2">
              {searchResults.map((result, index) => (
                <li
                  key={index}
                  className="p-4 border border-gray-500 rounded-lg bg-gray-600 text-gray-200 hover:bg-gray-500 transition cursor-pointer"
                  onClick={async () => {
                    const token = localStorage.getItem("token");
                    const chatResponse = await axios.get(
                      `${API_BASE_URL}/zhadiger/api/chats/by-llm/${result.text_id}`,
                      {
                        headers: {
                          Authorization: `${token}`,
                          "ngrok-skip-browser-warning": "true",
                        },
                        withCredentials: true,
                      }
                    );
                    setCurrentChatId(chatResponse.data.id);
                    loadChat(chatResponse.data.id);
                  }}
                >
                  <p>
                    <strong>{t("text_id")}:</strong> {result.text_id}
                  </p>
                  <p>
                    <strong>{t("text_score")}:</strong> {result.score}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};
export default ChatInterface;
